import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDrivers } from "@/components/TaskManager";
import { SubSection } from "@/components/FormSection";
import { IconBanknote, IconCalendar, IconMap, IconUsers } from "@/components/icons";
import { DateField, DateTimeField, Labeled, Select, TextArea, TextField } from "@/components/Form";
import { AttractionPicker } from "@/components/AttractionPicker";
import { tServiceKind, tStatus } from "@/i18n";
import {
  CAPACITY_PRESETS,
  COLLECT_CURRENCIES,
  VEHICLE_TYPES,
  EMIRATES,
  EXCURSION_TYPES,
  EXCURSION_TYPE_CAPACITY,
  hasExcursionType,
  isOwnDriver,
  isTicketsKind,
  isTourTypeKind,
  pricedPerGuest,
  SERVICE_KINDS,
  EMPTY_TRANSFER_GUIDE,
  defaultPaymentCollector,
  hasTransferGuide,
  isExcursionKind,
  isGuideKind,
  isYachtKind,
  lineDraftProblem,
  rebasePickupTime,
  toMinor,
  type LineDraft,
} from "@/lib/bookingLines";
import type {
  GuidePaymentCollector,
  BookingLinePaymentCollector,
  BookingLineServiceCategory,
  BookingLineServiceKind,
} from "@/lib/types";

const LOCATION_KINDS = ["airport", "hotel", "other"];
const TRANSPORT_TYPES = ["private", "sharing"];
const SERVICE_CATEGORIES: BookingLineServiceCategory[] = [
  "premium",
  "standard",
  "vip",
  "other",
];
const PAYMENT_COLLECTORS: BookingLinePaymentCollector[] = ["driver", "owner_direct"];
const GUIDE_PAYMENT_COLLECTORS: GuidePaymentCollector[] = ["guide", "owner_direct"];
const CURRENCIES = ["AED", "USD", "EUR", "RUB", "SAR"];

// Display-only preview of the server's default USD->AED rate (storage/fx.py);
// the authoritative figure always comes back from the API after save.
const FX_PREVIEW_RATE = 3.6725;

function toAedPreview(minor: number, currency: string): number {
  return currency === "USD" ? Math.round(minor * FX_PREVIEW_RATE) : minor;
}

/** The figures of one line — supplier, sale (AED), margin — computed from the
 * in-progress draft. Display only: the authoritative figures always come back
 * from the API after save. */
export interface LineFigures {
  kind: "yacht" | "guide" | "standard";
  /** How the supplier total is made up («400 + 4×50», «100 × 3 ч»). */
  supplierText: string;
  supplier: number;
  sale: number;
  margin: number;
  /** «600 AED» or, for a USD line, «163 $ ≈ 600 AED». */
  saleLabel: string;
  /** False when the line's currency is neither AED nor USD — the preview then
   * shows the number as typed, not converted. */
  converted: boolean;
}

function saleLabelOf(saleAed: number, currency: string): string {
  return currency === "USD"
    ? `${(saleAed / FX_PREVIEW_RATE / 100).toFixed(0)} $ ≈ ${(saleAed / 100).toFixed(0)} AED`
    : `${(saleAed / 100).toFixed(0)} AED`;
}

export function lineFigures(d: LineDraft, t: (k: string) => string): LineFigures {
  const pax = Number(d.pax_count) || 0;
  const perPersonSupplier = toMinor(d.supplier_price_per_person) ?? 0;
  const converted = d.currency === "AED" || d.currency === "USD";
  const isSharing =
    d.transport_type === "sharing" &&
    !hasExcursionType(d) &&
    d.service_kind !== "transfer" &&
    !isYachtKind(d.service_kind) &&
    !isGuideKind(d.service_kind);

  if (isYachtKind(d.service_kind)) {
    const hours = Number(d.duration_hours) || 0;
    const perHourSupplier = toMinor(d.supplier_price_per_vehicle) ?? 0;
    const perHourSale = toMinor(d.sale_price_per_person) ?? 0;
    const supplier = Math.round(perHourSupplier * hours);
    const sale = toAedPreview(Math.round(perHourSale * hours), d.currency);
    return {
      kind: "yacht",
      supplierText: `${(perHourSupplier / 100).toFixed(0)} × ${hours} ${t("bookingLines.hoursShort")}`,
      supplier,
      sale,
      margin: sale - supplier,
      saleLabel: saleLabelOf(sale, d.currency),
      converted,
    };
  }

  if (pricedPerGuest(d)) {
    const adults = Number(d.adult_count) || 0;
    const children = Number(d.child_count) || 0;
    const sa = toMinor(d.supplier_price_per_person) ?? 0;
    const sc = toMinor(d.supplier_price_per_child) ?? 0;
    const supplier = sa * adults + sc * children;
    const sale = toAedPreview(
      (toMinor(d.sale_price_per_person) ?? 0) * adults +
        (toMinor(d.sale_price_per_child) ?? 0) * children,
      d.currency,
    );
    return {
      kind: "standard",
      supplierText:
        children > 0
          ? `${adults}×${(sa / 100).toFixed(0)} + ${children}×${(sc / 100).toFixed(0)}`
          : `${adults}×${(sa / 100).toFixed(0)}`,
      supplier,
      sale,
      margin: sale - supplier,
      saleLabel: saleLabelOf(sale, d.currency),
      converted,
    };
  }

  if (isGuideKind(d.service_kind)) {
    const supplier = toMinor(d.supplier_price_per_vehicle) ?? 0;
    const sale = toAedPreview(toMinor(d.sale_price) ?? 0, d.currency);
    return {
      kind: "guide",
      supplierText: `${(supplier / 100).toFixed(0)}`,
      supplier,
      sale,
      margin: sale - supplier,
      saleLabel: saleLabelOf(sale, d.currency),
      converted,
    };
  }

  let supplierText: string;
  let supplier: number;
  let sale: number;
  if (isSharing) {
    supplier = perPersonSupplier * pax;
    supplierText = `${pax}×${(perPersonSupplier / 100).toFixed(0)}`;
    const perPersonSale = toMinor(d.sale_price_per_person) ?? 0;
    sale = toAedPreview(perPersonSale * pax, d.currency);
  } else {
    const perVehicle = toMinor(d.supplier_price_per_vehicle) ?? 0;
    supplier = perVehicle + perPersonSupplier * pax;
    supplierText =
      perPersonSupplier > 0
        ? `${(perVehicle / 100).toFixed(0)} + ${pax}×${(perPersonSupplier / 100).toFixed(0)}`
        : `${(perVehicle / 100).toFixed(0)}`;
    sale = toAedPreview(toMinor(d.sale_price) ?? 0, d.currency);
  }
  return {
    kind: "standard",
    supplierText,
    supplier,
    sale,
    margin: sale - supplier,
    saleLabel: saleLabelOf(sale, d.currency),
    converted,
  };
}

/** The whole line's supplier / sale / margin: a transfer with «+ Гид» adds the
 * guide to the car (the same figures as the «Итого» row of its preview). */
export function lineTotals(d: LineDraft, t: (k: string) => string): LineFigures {
  const f = lineFigures(d, t);
  if (!hasTransferGuide(d)) return f;
  const carCost = toMinor(d.supplier_price_per_vehicle) ?? 0;
  const carSale = toAedPreview(toMinor(d.sale_price) ?? 0, d.currency);
  const guideCost = toMinor(d.guide_pay) ?? 0;
  const guideSale = toAedPreview(toMinor(d.guide_sale) ?? 0, d.currency);
  const supplier = carCost + guideCost;
  const sale = carSale + guideSale;
  return { ...f, supplier, sale, margin: sale - supplier, saleLabel: saleLabelOf(sale, d.currency) };
}

/** A live "Поставщику: 400 + 4×50 = 600 AED · Продажа: ... · Маржа: ..."
 * preview computed from the in-progress draft. */
function calcPreview(d: LineDraft, t: (k: string) => string): string {
  const f = lineFigures(d, t);
  // Every figure is named — a bare "0 = 0 AED · 0 AED · 0 AED" says nothing.
  const head =
    f.kind === "guide"
      ? `${t("bookingLines.guidePay")}: ${(f.supplier / 100).toFixed(0)} AED · `
      : `${t("bookingLines.previewSupplier")}: ${f.supplierText} = ${(f.supplier / 100).toFixed(0)} AED · `;
  return (
    head +
    `${t("bookingLines.previewSale")}: ${f.saleLabel} · ` +
    `${t("bookingLines.margin")}: ${(f.margin / 100).toFixed(0)} AED`
  );
}

/** «2 × 250 + 1 × 150 = 650 AED» — the per-guest sum, as the owner reads it. */
export function guestSumText(d: LineDraft, t: (k: string) => string): string {
  const adults = Number(d.adult_count) || 0;
  const children = Number(d.child_count) || 0;
  const pa = toMinor(d.sale_price_per_person) ?? 0;
  const pc = toMinor(d.sale_price_per_child) ?? 0;
  const n = (minor: number) => (minor / 100).toFixed(0);
  const parts = [`${adults} ${t("bookingLines.adultsShort")} × ${n(pa)}`];
  if (children > 0) parts.push(`${children} ${t("bookingLines.childrenShort")} × ${n(pc)}`);
  return `${t("bookingLines.previewTotal")}: ${parts.join(" + ")} = ${n(pa * adults + pc * children)} ${d.currency}`;
}

function aed(minor: number): string {
  return `${(minor / 100).toFixed(0)} AED`;
}

/** Phase 14.3: the «Цена» block of a transfer with «+ Гид» — the car, the guide
 * and the whole line, each «поставщику · продажа · маржа» (all AED). */
export function transferGuidePreview(d: LineDraft, t: (k: string) => string): string[] {
  const carCost = toMinor(d.supplier_price_per_vehicle) ?? 0;
  const carSale = toAedPreview(toMinor(d.sale_price) ?? 0, d.currency);
  const guideCost = toMinor(d.guide_pay) ?? 0;
  const guideSale = toAedPreview(toMinor(d.guide_sale) ?? 0, d.currency);
  const row = (label: string, costLabel: string, cost: number, sale: number) =>
    `${label}: ${costLabel} ${aed(cost)} · ${t("bookingLines.previewSaleLower")} ${aed(sale)} · ` +
    `${t("bookingLines.previewMarginLower")} ${aed(sale - cost)}`;
  return [
    row(t("bookingLines.previewCar"), t("bookingLines.previewToSupplier"), carCost, carSale),
    row(t("bookingLines.previewGuide"), t("bookingLines.previewToGuide"), guideCost, guideSale),
    row(
      t("bookingLines.previewTotal"),
      t("bookingLines.previewToSuppliers"),
      carCost + guideCost,
      carSale + guideSale,
    ),
  ];
}

/** Emirate dropdown. A stored value outside the list (older free-text data)
 * is kept as an extra option instead of being silently blanked. */
export function EmirateSelect({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  const { t } = useTranslation();
  const known = EMIRATES.some((e) => e.value === value);
  return (
    <Labeled label={label}>
      <Select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {EMIRATES.map((e) => (
          <option key={e.value} value={e.value}>
            {t(`emirates.${e.key}`)}
          </option>
        ))}
        {value && !known && <option value={value}>{value}</option>}
      </Select>
    </Labeled>
  );
}

/** A transfer's «Тип машины»: седан до 4 … автобус 50 мест (its seats are the
 * pax limit). An older transfer with only a capacity shows «не выбран». */
export function VehicleTypeField({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Labeled label={t("bookingLines.vehicleType")}>
      <Select
        aria-label={t("bookingLines.vehicleType")}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {value === "" && <option value="">{t("bookingLines.vehicleTypeNone")}</option>}
        {VEHICLE_TYPES.map((v) => (
          <option key={v.value} value={v.value}>
            {t(`bookingLines.vehicleTypes.${v.value}`)}
          </option>
        ))}
      </Select>
    </Labeled>
  );
}

/** "Vehicle capacity": 4 / 6 / 7 / other (other reveals a number field). */
export function VehicleCapacityField({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Yacht lines call it "Вместимость яхты (чел.)" instead of the default
   * "Вместимость машины" — same underlying field. */
  label?: string;
}) {
  const { t } = useTranslation();
  const [other, setOther] = useState(() => value !== "" && !CAPACITY_PRESETS.includes(value));
  const isOther = other || !CAPACITY_PRESETS.includes(value);
  const fieldLabel = label ?? t("bookingLines.vehicleCapacity");
  return (
    <Labeled label={fieldLabel}>
      <div className="flex gap-2">
        <Select
          aria-label={fieldLabel}
          value={isOther ? "other" : value}
          onChange={(e) => {
            if (e.target.value === "other") {
              setOther(true);
              onChange("");
            } else {
              setOther(false);
              onChange(e.target.value);
            }
          }}
        >
          {CAPACITY_PRESETS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
          <option value="other">{t("bookingLines.capacityOther")}</option>
        </Select>
        {isOther && (
          <TextField
            aria-label={t("bookingLines.capacityOtherValue")}
            type="number"
            min="1"
            step="1"
            className="w-24"
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        )}
      </div>
    </Labeled>
  );
}

/** The controlled field grid shared by the booking-detail "add line" form, the
 * multi-line quick-create editor and the Phase 13.2 edit modal. ``grouped``
 * (the quick-create page) lays the same fields out in four sub-blocks —
 * «Услуга и дата», «Маршрут», «Пассажиры и транспорт», «Цена и оплата» — with
 * the price as three figure cards; without it the flat grid is unchanged. */
export function LineFields({
  value,
  onChange,
  showDriver = true,
  lockKind = false,
  grouped = false,
}: {
  value: LineDraft;
  onChange: (patch: Partial<LineDraft>) => void;
  showDriver?: boolean;
  /** Editing an existing line: the service kind is not changeable. */
  lockKind?: boolean;
  grouped?: boolean;
}) {
  const { t } = useTranslation();
  const drivers = useDrivers();
  const isTransfer = value.service_kind === "transfer";
  const isYacht = isYachtKind(value.service_kind);
  const isGuide = isGuideKind(value.service_kind);
  const isExcursion = isExcursionKind(value.service_kind) && !isYacht;
  const isTourType = isTourTypeKind(value.service_kind);
  const typed = hasExcursionType(value);
  const isTickets = isTicketsKind(value.service_kind);
  // Group excursion / tickets: «взрослых × цена + детей × цена».
  const guestPriced = pricedPerGuest(value) || isTickets;
  const isSharing =
    !isTransfer && !isYacht && !isGuide && !typed && !isTickets && value.transport_type === "sharing";
  const problem = lineDraftProblem(value);
  const set = <K extends keyof LineDraft>(k: K, v: LineDraft[K]) =>
    onChange({ [k]: v } as Partial<LineDraft>);

  // ---- the fields, built once -------------------------------------------
  const kindField = (
    <Labeled label={t("bookingLines.serviceKind")}>
      <Select
        value={value.service_kind}
        disabled={lockKind}
        onChange={(e) => {
          const kind = e.target.value as BookingLineServiceKind;
          onChange({
            service_kind: kind,
            payment_collector: defaultPaymentCollector(kind, value.yacht_transfer_needed),
          });
        }}
      >
        {SERVICE_KINDS.map((k) => (
          <option key={k} value={k}>
            {tServiceKind(k)}
          </option>
        ))}
      </Select>
    </Labeled>
  );
  const dateField = (
    <Labeled
      label={t("bookingLines.serviceDate")}
      required
      invalid={value.service_date.trim() === ""}
      error={t("common.required")}
    >
      <DateField
        value={value.service_date}
        onChange={(v) =>
          // A pickup time picked earlier follows the date, so moving a trip
          // to another day never leaves the dispatch task on the old one.
          onChange({
            service_date: v,
            pickup_time: rebasePickupTime(value.pickup_time, value.service_date, v),
          })
        }
      />
    </Labeled>
  );
  const timeField = (
    <Labeled label={t(isGuide ? "bookingLines.guideStartTime" : "bookingLines.pickupTime")}>
      <DateTimeField value={value.pickup_time} onChange={(v) => set("pickup_time", v)} />
    </Labeled>
  );
  const text = (label: string, key: keyof LineDraft, extra: { required?: boolean } = {}) => (
    <Labeled label={label} {...extra}>
      <TextField
        value={value[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
      />
    </Labeled>
  );
  const locationKind = (key: "from_kind" | "to_kind") => (
    <Labeled label={t("bookingLines.locationKind")}>
      <Select value={value[key]} onChange={(e) => set(key, e.target.value)}>
        <option value="">—</option>
        {LOCATION_KINDS.map((k) => (
          <option key={k} value={k}>
            {tStatus(k)}
          </option>
        ))}
      </Select>
    </Labeled>
  );
  const fromField = text(t("bookingLines.from"), "from_location");
  const fromKind = locationKind("from_kind");
  const fromEmirate = text(t("bookingLines.emirate"), "from_emirate");
  const toField = text(t("bookingLines.to"), "to_location");
  const toKind = locationKind("to_kind");
  const toEmirate = text(t("bookingLines.emirate"), "to_emirate");
  const flightField = text(t("bookingLines.flightNumber"), "flight_number");
  const transferGuide = <TransferGuideBlock value={value} onChange={onChange} />;
  const serviceNameField = text(t("bookingLines.serviceName"), "service_name");
  const marinaField = text(t("bookingLines.marinaLocation"), "marina_location");
  const rentalHoursField = (
    <Labeled
      label={t("bookingLines.rentalHours")}
      required
      invalid={problem === "hours"}
      error={t("common.required")}
    >
      <TextField
        type="number"
        min="1"
        step="0.5"
        value={value.duration_hours}
        onChange={(e) => set("duration_hours", e.target.value)}
      />
    </Labeled>
  );
  const yachtTransferToggle = (
    <div className="sm:col-span-3">
      <label className="flex items-center gap-2 text-sm text-sand">
        <input
          type="checkbox"
          checked={value.yacht_transfer_needed}
          onChange={(e) =>
            // Turning the transfer off drops a driver picked for it.
            onChange(
              e.target.checked
                ? { yacht_transfer_needed: true, payment_collector: "driver" }
                : {
                    yacht_transfer_needed: false,
                    driver_guide_id: "",
                    payment_collector: "owner_direct",
                  },
            )
          }
        />
        {t("bookingLines.yachtTransferNeeded")}
      </label>
    </div>
  );
  const pickupLocationField = text(t("bookingLines.pickupLocation"), "pickup_location");
  const pickupAddressField = text(t("bookingLines.pickupAddress"), "pickup_address");
  // Excursions keep only this from the route: the hotel / address of the pickup.
  const excursionPickupField = text(t("bookingLines.excursionPickup"), "pickup_location");
  const pickupEmirateField = (
    <EmirateSelect
      label={t("bookingLines.pickupEmirate")}
      value={value.from_emirate}
      onChange={(v) => set("from_emirate", v)}
    />
  );
  const destinationEmirateField = (
    <EmirateSelect
      label={t("bookingLines.destinationEmirate")}
      value={value.to_emirate}
      onChange={(v) => set("to_emirate", v)}
    />
  );
  const baggageField = (
    <>
      <Labeled
        label={t("bookingLines.baggageCount")}
        invalid={problem === "baggage"}
        error={t("bookingLines.baggageCountInvalid")}
      >
        <TextField
          type="number" min="0" step="1" placeholder="0"
          value={value.baggage_count}
          onChange={(e) => set("baggage_count", e.target.value)}
        />
      </Labeled>
      {text(t("bookingLines.baggage"), "baggage")}
    </>
  );
  const guideHoursField = (
    <Labeled label={t("bookingLines.guideHours")}>
      <TextField
        type="number"
        min="0"
        step="0.5"
        value={value.duration_hours}
        onChange={(e) => set("duration_hours", e.target.value)}
      />
    </Labeled>
  );
  const guideLanguageField = (
    <Labeled label={t("bookingLines.guideLanguage")}>
      <Select value={value.guide_language} onChange={(e) => set("guide_language", e.target.value)}>
        <option value="">—</option>
        <option value="ru">RU</option>
        <option value="en">EN</option>
        <option value="other">{tStatus("other")}</option>
      </Select>
    </Labeled>
  );
  const meetingPlaceField = text(t("bookingLines.meetingPlace"), "pickup_location");
  const categoryField = (
    <Labeled label={t("bookingLines.serviceCategory")}>
      <Select
        value={value.service_category}
        onChange={(e) => set("service_category", e.target.value)}
      >
        <option value="">—</option>
        {SERVICE_CATEGORIES.map((k) => (
          <option key={k} value={k}>
            {tStatus(k)}
          </option>
        ))}
      </Select>
    </Labeled>
  );
  const money = (label: string, key: keyof LineDraft) => (
    <Labeled label={label}>
      <TextField
        type="number" min="0" step="0.01" placeholder="0.00"
        value={value[key] as string}
        onChange={(e) => set(key, e.target.value as never)}
      />
    </Labeled>
  );
  const excursionTypeField = (
    <Labeled label={t("bookingLines.excursionType")} required>
      <Select
        value={value.excursion_type}
        onChange={(e) => {
          const type = e.target.value;
          // A group starts from the party already typed (all adults), else 1 adult.
          const pax = value.pax_count.trim();
          const emptyCounts = value.adult_count.trim() === "" && value.child_count.trim() === "";
          onChange({
            excursion_type: type,
            ...(type === "group" && (pax || emptyCounts)
              ? { adult_count: pax || "1", child_count: "0" }
              : {}),
          });
        }}
      >
        {value.excursion_type === "" && (
          // an older line: still its old category until a type is picked
          <option value="">
            {value.service_category ? `— (${tStatus(value.service_category)})` : "—"}
          </option>
        )}
        {EXCURSION_TYPES.map((k) => (
          <option key={k} value={k}>
            {t(`bookingLines.excursionTypes.${k}`)}
          </option>
        ))}
      </Select>
    </Labeled>
  );
  const attractionField = (
    <Labeled label={t("bookingLines.attraction")}>
      <AttractionPicker value={value.service_name} onChange={(v) => set("service_name", v)} />
    </Labeled>
  );
  const guestsInvalid = problem === "guests";
  const count = (label: string, key: "adult_count" | "child_count") => (
    <Labeled label={label} invalid={guestsInvalid} error={t("bookingLines.guestsRequired")}>
      <TextField
        type="number"
        min="0"
        step="1"
        value={value[key]}
        onChange={(e) => set(key, e.target.value)}
      />
    </Labeled>
  );
  const guestPriceRows = (
    <>
      {count(t("bookingLines.adults"), "adult_count")}
      {money(t("bookingLines.salePerAdult"), "sale_price_per_person")}
      {money(t("bookingLines.supplierPerAdult"), "supplier_price_per_person")}
      {count(t("bookingLines.children"), "child_count")}
      {money(t("bookingLines.salePerChild"), "sale_price_per_child")}
      {money(t("bookingLines.supplierPerChild"), "supplier_price_per_child")}
    </>
  );
  const guestSum = guestPriced && (
    <p className="mt-2 text-sm text-ink" data-testid="guest-sum">
      {guestSumText(value, t)}
    </p>
  );
  const paxField = (
    <Labeled label={t("bookingLines.pax")}>
      <TextField
        type="number"
        min="0"
        value={value.pax_count}
        onChange={(e) => set("pax_count", e.target.value)}
      />
    </Labeled>
  );
  const transportField = !isYacht && !isGuide && (
    <Labeled label={t("bookingLines.transportType")}>
      <Select
        value={value.transport_type}
        disabled={isTransfer}
        onChange={(e) => set("transport_type", e.target.value)}
      >
        {TRANSPORT_TYPES.map((k) => (
          <option key={k} value={k}>
            {k === "private" ? t("bookingLines.privateCar") : tStatus(k)}
          </option>
        ))}
      </Select>
    </Labeled>
  );
  const lineBaggageField = !isYacht && !isGuide && baggageField;
  const passengerNameField = (
    <Labeled label={t("bookingLines.passengerName")}>
      <TextField
        value={value.passenger_name}
        onChange={(e) =>
          onChange({ passenger_name: e.target.value, passenger_name_touched: true })
        }
      />
    </Labeled>
  );
  const whatsappField = text(t("bookingLines.passengerWhatsapp"), "passenger_whatsapp");
  const currencyField = (
    <Labeled label={t("fields.currency")}>
      <Select value={value.currency} onChange={(e) => set("currency", e.target.value)}>
        {CURRENCIES.map((c) => (
          <option key={c}>{c}</option>
        ))}
      </Select>
    </Labeled>
  );
  const priceInputs = isYacht ? (
    <>
      {money(t("bookingLines.supplierPerHour"), "supplier_price_per_vehicle")}
      {money(t("bookingLines.salePricePerHour"), "sale_price_per_person")}
      <VehicleCapacityField
        label={t("bookingLines.yachtCapacity")}
        value={value.vehicle_capacity}
        onChange={(v) => set("vehicle_capacity", v)}
      />
      {currencyField}
    </>
  ) : isGuide ? (
    <>
      {money(t("bookingLines.guidePay"), "supplier_price_per_vehicle")}
      {money(t("bookingLines.salePriceClient"), "sale_price")}
      {currencyField}
    </>
  ) : guestPriced ? (
    <>
      {guestPriceRows}
      {currencyField}
    </>
  ) : isSharing ? (
    <>
      {money(t("bookingLines.supplierPerPerson"), "supplier_price_per_person")}
      {money(t("bookingLines.salePricePerPerson"), "sale_price_per_person")}
      {currencyField}
    </>
  ) : (
    <>
      {money(t("bookingLines.supplierPerVehicle"), "supplier_price_per_vehicle")}
      {!isTransfer && money(t("bookingLines.supplierPerPerson"), "supplier_price_per_person")}
      {/* a transfer: the seats come from «Тип машины»; a typed private
          excursion: from its type */}
      {isTransfer ? (
        <VehicleTypeField value={value.vehicle_type} onChange={(v) => set("vehicle_type", v)} />
      ) : (
        !typed && (
          <VehicleCapacityField
            value={value.vehicle_capacity}
            onChange={(v) => set("vehicle_capacity", v)}
          />
        )
      )}
      <Labeled label={t(typed ? "bookingLines.salePricePerCar" : "bookingLines.salePrice")}>
        <div className="flex gap-2">
          <TextField
            type="number" min="0" step="0.01" placeholder="0.00"
            value={value.sale_price}
            onChange={(e) => set("sale_price", e.target.value)}
          />
          <Select
            className="w-24"
            value={value.currency}
            onChange={(e) => set("currency", e.target.value)}
          >
            {CURRENCIES.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </Select>
        </div>
      </Labeled>
    </>
  );
  const guidePreview = hasTransferGuide(value) && (
    <div className="mt-2 space-y-0.5 text-xs text-sand-muted" data-testid="guide-preview">
      {transferGuidePreview(value, t).map((row, i) => (
        <p key={i} className={i === 2 ? "text-sand" : undefined}>
          {row}
        </p>
      ))}
    </div>
  );
  const capacityProblem = (problem === "capacity" || problem === "pax_over_capacity") && (
    <p role="alert" className="mt-1 text-xs text-red-700">
      {problem === "capacity"
        ? t("bookingLines.capacityInvalid")
        : typed
          ? t("bookingLines.paxOverType", { seats: EXCURSION_TYPE_CAPACITY[value.excursion_type] })
          : t("bookingLines.paxOverCapacity")}
    </p>
  );
  const collectorField = (
    <Labeled
      label={t(
        hasTransferGuide(value)
          ? "bookingLines.paymentCollectorCar"
          : "bookingLines.paymentCollector",
      )}
    >
      <Select
        value={value.payment_collector}
        onChange={(e) =>
          set("payment_collector", e.target.value as LineDraft["payment_collector"])
        }
      >
        {PAYMENT_COLLECTORS.map((k) => (
          <option key={k} value={k}>
            {k === "driver"
              ? t(isGuide ? "bookingLines.collectorGuide" : "bookingLines.collectorDriver")
              : t("bookingLines.collectorOwnerDirect")}
          </option>
        ))}
      </Select>
    </Labeled>
  );
  // The amount's currency is said next to it (AED / USD — migration 0065), so an
  // AED figure is never read as USD (184 «AED» became 184 USD); left as is it is
  // the line's own currency.
  const collectCurrency = value.collect_currency || value.currency || "AED";
  const collectCurrencies = COLLECT_CURRENCIES.includes(value.currency || "AED")
    ? COLLECT_CURRENCIES
    : [value.currency, ...COLLECT_CURRENCIES];
  const collectAmountField = value.payment_collector === "driver" && (
    <Labeled label={t("bookingLines.collectAmount")}>
      <div className="flex gap-2">
        <TextField
          type="number" min="0" step="0.01"
          placeholder={t("bookingLines.collectAmountPlaceholder")}
          value={value.collect_amount}
          onChange={(e) => set("collect_amount", e.target.value)}
        />
        <Select
          className="w-24"
          aria-label={t("bookingLines.collectCurrency")}
          value={collectCurrency}
          onChange={(e) => set("collect_currency", e.target.value)}
        >
          {collectCurrencies.map((c) => (
            <option key={c}>{c}</option>
          ))}
        </Select>
      </div>
    </Labeled>
  );
  // A yacht with no marina transfer has nobody to send — no driver picker.
  const allDrivers = drivers.data?.items ?? [];
  const pickedOwn = isOwnDriver(allDrivers.find((d) => d.id === value.driver_guide_id));
  const driverField = showDriver && !(isYacht && !value.yacht_transfer_needed) && (
    <Labeled label={t(isGuide ? "bookingLines.guidePicker" : "bookingLines.driver")}>
      <Select
        value={value.driver_guide_id}
        onChange={(e) => {
          const id = e.target.value;
          // Our own driver is not paid per trip: «Оплата водителю» → 0.
          const own = isOwnDriver(allDrivers.find((d) => d.id === id));
          onChange({
            driver_guide_id: id,
            ...(own && !guestPriced && !isYacht ? { supplier_price_per_vehicle: "" } : {}),
          });
        }}
      >
        <option value="">—</option>
        {allDrivers
          .filter((d) => d.status === "active")
          .filter((d) => !isGuide || d.kind === "guide")
          .map((d) => (
            <option key={d.id} value={d.id}>
              {d.name}
              {isOwnDriver(d) ? ` · ${t("drivers.own")}` : ""}
            </option>
          ))}
      </Select>
      {pickedOwn && (
        <span className="mt-1 block text-xs text-ink-muted">{t("bookingLines.ownDriverHint")}</span>
      )}
    </Labeled>
  );
  const notesField = (
    <div className="sm:col-span-3">
      <Labeled label={t("fields.notes")}>
        <TextArea value={value.notes} onChange={(e) => set("notes", e.target.value)} />
      </Labeled>
    </div>
  );

  if (grouped) {
    const totals = lineTotals(value, t);
    const hasRoute = isTransfer || isExcursion || (isYacht && value.yacht_transfer_needed);
    const grid = "grid gap-4 sm:grid-cols-3";
    return (
      <div className="space-y-5">
        <SubSection
          first
          icon={<IconCalendar size={16} />}
          tone="blue"
          title={t("quickCreate.serviceDate")}
          hint={t("quickCreate.serviceDateHint")}
        >
          <div className={grid}>
            {kindField}
            {dateField}
            {timeField}
            {isYacht ? (
              <>
                {serviceNameField}
                {marinaField}
                {rentalHoursField}
                {yachtTransferToggle}
              </>
            ) : isGuide ? (
              <>
                {serviceNameField}
                {guideHoursField}
                {guideLanguageField}
                {meetingPlaceField}
              </>
            ) : isTourType ? (
              <>
                {serviceNameField}
                {excursionTypeField}
              </>
            ) : isTickets ? (
              attractionField
            ) : !isTransfer ? (
              <>
                {serviceNameField}
                {categoryField}
              </>
            ) : null}
          </div>
        </SubSection>

        {hasRoute && (
          <SubSection
            icon={<IconMap size={16} />}
            tone="violet"
            title={t("quickCreate.route")}
            hint={t("quickCreate.routeHint")}
          >
            {isTransfer ? (
              <div className="space-y-3">
                <div className={grid}>
                  {fromField}
                  {fromKind}
                  {fromEmirate}
                </div>
                <div className="flex justify-center text-lg font-bold text-[#5B3FA8]" aria-hidden="true">
                  ↓
                </div>
                <div className={grid}>
                  {toField}
                  {toKind}
                  {toEmirate}
                </div>
                <div className={grid}>{flightField}</div>
              </div>
            ) : isYacht ? (
              <div className={grid}>
                {pickupLocationField}
                {pickupAddressField}
                {pickupEmirateField}
                {destinationEmirateField}
                {baggageField}
              </div>
            ) : (
              // an excursion: only where to pick the guests up
              <div className={grid}>{excursionPickupField}</div>
            )}
          </SubSection>
        )}

        {!isTransfer && !isExcursion && !isYacht && !isGuide && !isTickets && (
          // a non-excursion service (tickets, visa, other): pickup without a route block
          <div className={grid}>
            {pickupLocationField}
            {pickupAddressField}
          </div>
        )}

        <SubSection
          icon={<IconUsers size={16} />}
          tone="orange"
          title={t("quickCreate.passengers")}
          hint={t("quickCreate.passengersHint")}
        >
          <div className={grid}>
            {!guestPriced && paxField}
            {!typed && !isTickets && transportField}
            {!isTickets && lineBaggageField}
            {passengerNameField}
            {whatsappField}
            {isTransfer && transferGuide}
          </div>
        </SubSection>

        <SubSection
          icon={<IconBanknote size={16} />}
          tone="green"
          title={t("quickCreate.price")}
          hint={t("quickCreate.priceHint")}
        >
          <div className="rounded-[14px] border-[1.5px] border-[#2A9D8F] bg-[#F3FAF8] p-4 sm:p-5">
            <div className={grid}>{priceInputs}</div>
            {guestSum}
            <div className="mt-4 grid gap-3 sm:grid-cols-3" data-testid="price-figures">
              <Figure
                label={t("bookingLines.previewSupplier")}
                value={totals.supplier}
                // the make-up only when it says more than the total («400 + 4×50»)
                note={/[+×]/.test(totals.supplierText) && !hasTransferGuide(value) ? totals.supplierText : undefined}
                tone="grey"
              />
              <Figure
                label={t("bookingLines.previewSale")}
                value={totals.sale}
                note={value.currency === "USD" ? totals.saleLabel : undefined}
                tone="teal"
              />
              <Figure
                label={t("bookingLines.margin")}
                value={totals.margin}
                tone={totals.margin > 0 ? "green" : totals.margin < 0 ? "red" : "grey"}
              />
            </div>
            {!totals.converted && (
              <p className="mt-2 text-xs text-ink-muted">
                {t("quickCreate.notConverted", { currency: value.currency })}
              </p>
            )}
            {guidePreview}
            {capacityProblem}
          </div>
          <div className={grid + " mt-4"}>
            {collectorField}
            {collectAmountField}
            {driverField}
          </div>
        </SubSection>

        <div className="border-t border-[#D4ECE7] pt-5">{notesField}</div>
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-3">
      {kindField}
      {dateField}
      {timeField}

      {isTransfer ? (
        <>
          {fromField}
          {fromKind}
          {fromEmirate}
          {toField}
          {toKind}
          {toEmirate}
          {flightField}
          {transferGuide}
        </>
      ) : isYacht ? (
        <>
          {serviceNameField}
          {marinaField}
          {rentalHoursField}
          {yachtTransferToggle}
          {value.yacht_transfer_needed && (
            <>
              {pickupLocationField}
              {pickupAddressField}
              {pickupEmirateField}
              {destinationEmirateField}
              {baggageField}
            </>
          )}
        </>
      ) : isGuide ? (
        <>
          {serviceNameField}
          {guideHoursField}
          {guideLanguageField}
          {meetingPlaceField}
        </>
      ) : isTourType ? (
        <>
          {serviceNameField}
          {excursionTypeField}
          {excursionPickupField}
        </>
      ) : isTickets ? (
        attractionField
      ) : (
        <>
          {serviceNameField}
          {categoryField}
          {pickupLocationField}
          {pickupAddressField}
        </>
      )}

      {!guestPriced && paxField}
      {!typed && !isTickets && transportField}
      {!isTickets && lineBaggageField}
      {passengerNameField}
      {whatsappField}

      <div className="sm:col-span-3 rounded-md border border-olive-600 bg-olive-900 p-3">
        <div className="mb-2 text-xs font-semibold text-sand-muted">
          {t("bookingLines.pricing")}
        </div>
        <div className="grid gap-3 sm:grid-cols-3">{priceInputs}</div>
        {guestSum}
        {guidePreview || <p className="mt-2 text-xs text-sand-muted">{calcPreview(value, t)}</p>}
        {capacityProblem}
      </div>

      <div className="sm:col-span-3 grid gap-3 sm:grid-cols-3">
        {collectorField}
        {collectAmountField}
      </div>

      {driverField}
      {notesField}
    </div>
  );
}

const FIGURE_TONE = {
  grey: "bg-[#EEF1F0] text-[#33403D]",
  teal: "bg-[#E0F2EF] text-[#1E6B65]",
  green: "bg-[#DCF5E6] text-[#166534]",
  red: "bg-[#FEECEC] text-[#B42318]",
} as const;

/** One of the three price cards: Поставщику / Продажа / Маржа (AED). */
function Figure({
  label,
  value,
  note,
  tone,
}: {
  label: string;
  value: number;
  note?: string;
  tone: keyof typeof FIGURE_TONE;
}) {
  return (
    <div className={`rounded-xl px-4 py-3 ${FIGURE_TONE[tone]}`}>
      <div className="text-xs font-semibold">{label}</div>
      <div className="mt-1 text-xl font-bold leading-tight">{(value / 100).toFixed(0)} AED</div>
      {note && <div className="mt-0.5 text-xs opacity-90">{note}</div>}
    </div>
  );
}

/** Phase 14.3: «+ Гид» on a transfer — the toggle and, when on, the guide's own
 * block inside the same line (the stand-alone «Гид» service is for a guide with
 * no car). Turning it off empties the block, so the guide counts nowhere. */
function TransferGuideBlock({
  value,
  onChange,
}: {
  value: LineDraft;
  onChange: (patch: Partial<LineDraft>) => void;
}) {
  const { t } = useTranslation();
  const drivers = useDrivers();
  const set = <K extends keyof LineDraft>(k: K, v: LineDraft[K]) =>
    onChange({ [k]: v } as Partial<LineDraft>);
  const guides = (drivers.data?.items ?? []).filter(
    (d) => d.kind === "guide" && (d.status === "active" || d.id === value.guide_driver_id),
  );
  const badPrice = toMinor(value.guide_pay) === null || toMinor(value.guide_sale) === null;
  return (
    <div className="sm:col-span-3">
      <label className="flex items-center gap-2 text-sm text-sand">
        <input
          type="checkbox"
          checked={value.guide_included}
          onChange={(e) =>
            onChange(
              e.target.checked
                ? { guide_included: true }
                : { ...EMPTY_TRANSFER_GUIDE, guide_language: "" },
            )
          }
        />
        {t("bookingLines.addGuideToTransfer")}
      </label>
      {value.guide_included && (
        <div
          className="mt-2 rounded-md border border-olive-600 bg-olive-900 p-3"
          data-testid="transfer-guide-block"
        >
          <div className="mb-2 text-xs font-semibold text-sand-muted">
            {t("bookingLines.transferGuideTitle")}
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <Labeled label={t("bookingLines.guidePicker")}>
              <Select
                value={value.guide_driver_id}
                onChange={(e) => set("guide_driver_id", e.target.value)}
              >
                <option value="">—</option>
                {guides.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Select>
            </Labeled>
            <Labeled label={t("bookingLines.guideLanguage")}>
              <Select
                value={value.guide_language}
                onChange={(e) => set("guide_language", e.target.value)}
              >
                <option value="">—</option>
                <option value="ru">RU</option>
                <option value="en">EN</option>
                <option value="other">{tStatus("other")}</option>
              </Select>
            </Labeled>
            <Labeled label={t("bookingLines.guideHours")}>
              <TextField
                type="number"
                min="0"
                step="0.5"
                value={value.guide_hours}
                onChange={(e) => set("guide_hours", e.target.value)}
              />
            </Labeled>
            <Labeled
              label={t("bookingLines.guidePayAed")}
              invalid={badPrice && toMinor(value.guide_pay) === null}
              error={t("bookingLines.amountInvalid")}
            >
              <TextField
                type="number" min="0" step="0.01" placeholder="0.00"
                value={value.guide_pay}
                onChange={(e) => set("guide_pay", e.target.value)}
              />
            </Labeled>
            <Labeled
              label={`${t("bookingLines.salePriceClient")} (${value.currency})`}
              invalid={badPrice && toMinor(value.guide_sale) === null}
              error={t("bookingLines.amountInvalid")}
            >
              <TextField
                type="number" min="0" step="0.01" placeholder="0.00"
                value={value.guide_sale}
                onChange={(e) => set("guide_sale", e.target.value)}
              />
            </Labeled>
            <Labeled label={t("bookingLines.guidePaymentCollector")}>
              <Select
                value={value.guide_payment_collector}
                onChange={(e) =>
                  set("guide_payment_collector", e.target.value as GuidePaymentCollector)
                }
              >
                {GUIDE_PAYMENT_COLLECTORS.map((k) => (
                  <option key={k} value={k}>
                    {k === "guide"
                      ? t("bookingLines.collectorGuide")
                      : t("bookingLines.collectorOwnerDirect")}
                  </option>
                ))}
              </Select>
            </Labeled>
          </div>
        </div>
      )}
    </div>
  );
}
